import { HealthSystemDALEnumsLocationStableType } from '../api/HealthSystemApi';
import { WindSensitivity } from './notification-rule';

/** The numeric stable types the API returns, as generated from swagger. */
export type LungWeatherStableType = HealthSystemDALEnumsLocationStableType;

/**
 * Stable type labels. Unknown (0) is the farm-wide fallback for a farm with no locations, so the review screen
 * says "whole farm" rather than naming a barn that does not exist.
 */
export const stableTypeLabels: Record<number, string> = {
  0: 'f-lung-weather-whole-farm',
  10: 'f-stable-fatting',
  20: 'f-stable-nursery',
  30: 'f-stable-farrowing',
  40: 'f-stable-gestation',
  50: 'f-stable-mating',
  60: 'f-stable-quarantine',
  70: 'f-stable-calm',
  80: 'f-stable-gilts',
  90: 'f-stable-breeding'
};

/**
 * What the lung/weather correlation has learned about one disease on one farm, what it thinks of this week, and
 * whether it has been right before.
 *
 * Mirrors LungWeatherReviewViewModel. Only wind is scored - the stored weather window also holds temperature,
 * humidity and pressure, but the API deliberately does not surface them here, because showing them would imply
 * they affect the verdict.
 */
export interface LungWeatherReview {
  farmId: string;
  farmName: string;
  diseaseId: string;
  diseaseName: string;
  /** Null when no lung/weather rule exists for this disease. */
  notificationRuleId?: string;
  sensitivity?: WindSensitivity;
  /** The score a match has to reach under the current sensitivity. */
  matchThreshold: number;
  /** The confidence a match has to reach before a warning is actually sent. */
  confidenceFloor: number;
  /** How many patterns a stable needs before sample size alone clears the floor - drives the learning meter. */
  patternsForConfidence: number;
  /** Empty until the first peak is detected, which is the normal first-season state. */
  stables: LungWeatherStable[];
}

export interface LungWeatherStable {
  stable: LungWeatherStableType;
  /** True when the farm has no locations, so patterns are farm-wide rather than per barn. */
  isFarmWide: boolean;
  latest?: LungWeatherCheck;
  /** Every stored pattern scored against the latest forecast, best first. */
  patterns: LungWeatherPattern[];
  /** Oldest first, for the track record chart. */
  history: LungWeatherHistoryPoint[];
}

export type LungWeatherVerdict = 'Matched' | 'Learning' | 'NoMatch' | 'Skipped';

export interface LungWeatherCheck {
  id: string;
  checkedAt: string;
  verdict: LungWeatherVerdict;
  skipReason?: string;
  matchScore: number;
  confidence: number;
  matchThreshold: number;
  confidenceFloor: number;
  forecastFrom?: string;
  forecastTill?: string;
  matchedPatternId?: string;
  notificationDispatched: boolean;
  notificationError?: string;
  forecast?: LungWeatherWind;
  measures: LungWeatherMeasure[];
}

export interface LungWeatherPattern {
  id: string;
  detectedAt: string;
  peakWindowFrom: string;
  peakWindowTill: string;
  peakInjectionCount: number;
  peakThreshold: number;
  attributedViaLocation: number;
  attributedViaAnimalHistory: number;
  unattributed: number;
  /** 0 to 1: how much this pattern counts towards confidence, from its size and attribution. */
  evidenceWeight: number;
  weatherFrom: string;
  weatherTill: string;
  wind: LungWeatherWind;
  /** Undefined when there is no check to score against yet. */
  scoreAgainstForecast?: number;
  isClosestMatch: boolean;
}

export interface LungWeatherWind {
  maxWindGustKmh: number;
  maxWindSpeedKmh: number;
  meanWindSpeedKmh: number;
  dominantWindDirectionDegrees: number;
  /** 0 to 1. Near zero means the direction is an artefact of averaging and is discounted. */
  windDirectionConstancy: number;
  windDirectionChangeCount: number;
}

export interface LungWeatherMeasure {
  measure: 'Strength' | 'Direction' | 'Shifts';
  forecastValue: number;
  patternValue: number;
  deviation: number;
  referenceSpread: number;
  similarity: number;
  /** Lower than the configured weight when constancy discounted it. */
  appliedWeight: number;
}

export interface LungWeatherHistoryPoint {
  id: string;
  checkedAt: string;
  verdict: LungWeatherVerdict;
  matchScore: number;
  confidence: number;
  /** Set when a real peak followed this warning. */
  confirmedAt?: string;
}

/** Compass label for a bearing, in the meteorological sense of where the wind comes from. */
export const bearingToCompass = (degrees: number): string => {
  const points = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

  return points[Math.round((((degrees % 360) + 360) % 360) / 22.5) % 16];
};
